import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["type", "showTitle", "showMessage", "messageI18nOptions"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedSize from 'I18n/components/FormattedSize';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import UILink from 'UIComponents/link/UILink';
import { LEARN_ABOUT_HS_VIDEO_LINK } from '../Constants';
import ApiErrorTypes from '../enums/ApiErrorTypes';
import FileUploadErrorType from '../enums/FileUploadErrorType';
import { MAX_FILE_UPLOAD_TOTAL } from '../constants/CrmPlatformLimits';
import { MAX_UPLOAD_SIZE_BYTES } from '../constants/fileSizes';
import { isJsonErrorResponse, reportError } from './logging';
import { splitNameAndExtension } from './file';
import { jsx as _jsx } from "react/jsx-runtime";
export const getAnimatedGifVideoThumbnailFailureNotification = () => {
  return {
    type: 'danger',
    titleText: I18n.text('FileManagerCore.notifications.animatedGifVideoThumbnail.error.title'),
    message: I18n.text('FileManagerCore.notifications.animatedGifVideoThumbnail.error.message')
  };
};
export const getVideoThumbnailNetworkRequestFailedNotification = () => {
  return {
    type: 'danger',
    titleText: I18n.text('FileManagerCore.notifications.videoThumbnail.error.title'),
    message: I18n.text('FileManagerCore.notifications.videoThumbnail.error.message')
  };
};
export const getFetchSignedURLFailedNotification = () => ({
  type: 'danger',
  titleText: I18n.text('FileManagerCore.notifications.fetchSignedURL.error.title'),
  message: I18n.text('FileManagerCore.notifications.fetchSignedURL.error.message')
});
export const getCanvaDownloadFailedNotification = fileName => ({
  type: 'danger',
  titleText: I18n.text('FileManagerCore.notifications.downloadFromCanva.error.title'),
  message: I18n.text('FileManagerCore.notifications.downloadFromCanva.error.message', {
    fileName
  })
});
export const getSuccessNotification = (notificationKey, _ref = {}) => {
  let {
      type = 'success',
      showTitle = true,
      showMessage = true,
      messageI18nOptions = {}
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const titleKey = `FileManagerCore.notifications.${notificationKey}.${type}.title`;
  return Object.assign({}, rest, {
    type,
    titleText: showTitle ? I18n.text(titleKey) : null,
    message: showMessage ? I18n.text(`FileManagerCore.notifications.${notificationKey}.${type}.message`, messageI18nOptions) : null
  });
};
export const getTranslatedText = (key, i18nOptions = {}) => {
  if (isValidI18nKey(key)) {
    return I18n.text(key, i18nOptions);
  }
  reportError('Found invalid translation key', {
    key
  });
  return null;
};
export const getFailureNotification = (notificationKey, {
  showTitle = true,
  messageI18nOptions = {}
} = {}) => {
  const titleKey = `FileManagerCore.notifications.${notificationKey}.error.title`;
  const messageKey = `FileManagerCore.notifications.${notificationKey}.error.message`;
  const titleText = showTitle ? getTranslatedText(titleKey) || I18n.text('FileManagerCore.notifications.general.error.title') : null;
  const message = getTranslatedText(messageKey, messageI18nOptions) || I18n.text('FileManagerCore.notifications.general.error.message');
  return {
    type: 'danger',
    titleText,
    message
  };
};
export const getSubtitlesFailureNotification = i18nKey => {
  return {
    type: 'danger',
    titleText: I18n.text(`FileManagerCore.notifications.${i18nKey}.error.title`)
  };
};
export const getSubtitlesSuccessNotification = i18nKey => {
  return {
    type: 'success',
    titleText: I18n.text(`FileManagerCore.notifications.${i18nKey}.success.title`)
  };
};
const getReadOnlyReasonKey = reason => `FileManagerCore.permissions.readOnlyReason.${reason}`;
export const getReadOnlyPermissionCopy = readOnlyReason => {
  //@ts-expect-error needs refactor to use FormattedMessage if possible
  return I18n.lookup(getReadOnlyReasonKey(readOnlyReason));
};
export const getReadOnlyPermissionMessaging = readOnlyReason => {
  const copyInfo = getReadOnlyPermissionCopy(readOnlyReason);
  if (!copyInfo) {
    return copyInfo;
  }
  const messaging = {};
  Object.keys(copyInfo).forEach(key => {
    messaging[key] = /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${getReadOnlyReasonKey(readOnlyReason)}.${key}`
    });
  });
  return messaging;
};
export const getFileUploadSuccessAlert = (fileVisibility, count, translateOptions) => ({
  'data-test-id': 'file-upload-success-alert',
  type: 'success',
  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: `FileManagerCore.alerts.upload.success.title`,
    options: {
      visibility: I18n.text(`FileManagerCore.alerts.fileAccess.${fileVisibility}`),
      count
    }
  }),
  message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `FileManagerCore.alerts.upload.success.text_jsx`,
    elements: {
      UILink
    },
    options: Object.assign({}, translateOptions, {
      count
    })
  })
});
export const partitioningAlerts = {
  success: {
    type: 'success',
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "FileManagerCore.alerts.folderPartitioning.success.title"
    }),
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "FileManagerCore.alerts.folderPartitioning.success.message"
    })
  },
  error: {
    type: 'danger',
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "FileManagerCore.alerts.folderPartitioning.error.title"
    })
  }
};
export const getVideo2OptInOutNotification = (notificationKey, type) => {
  return {
    type,
    titleText: I18n.text(`FileManagerCore.notifications.${notificationKey}.${type}.title`),
    message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      options: {
        href: LEARN_ABOUT_HS_VIDEO_LINK
      },
      elements: {
        Link: UILink
      },
      message: `FileManagerCore.notifications.${notificationKey}.${type}.message_jsx`
    })
  };
};
export function getErrorType(err) {
  if (err.responseJSON && typeof err.responseJSON === 'object' && 'errorType' in err.responseJSON && typeof err.responseJSON.errorType === 'string') {
    return err.responseJSON.errorType;
  }
  return null;
}
export function getUploadError(response) {
  let errorData = null;
  if (response.data) {
    if (typeof response.data === 'string') {
      try {
        errorData = JSON.parse(response.data);
      } catch (e) {} // eslint-disable-line no-empty
    } else if (typeof response.data === 'object') {
      errorData = response.data;
    }
  }
  return errorData;
}
export const getUploadFileErrorNotification = (error, file) => {
  const fileSize = file.size;
  const {
    name: fileName,
    extension
  } = splitNameAndExtension(file);
  const errorData = getUploadError(error);
  const errorType = errorData && errorData.errorType ? errorData.errorType : undefined;
  const maxSize = errorData && errorData.errorTokens && errorData.errorTokens.maxSize ? parseInt(errorData.errorTokens.maxSize[0], 10) : undefined;
  const titleText = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "FileManagerCore.notifications.upload.error.title"
  });
  if (errorData && error.status === 403) {
    return {
      titleText,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `FileManagerCore.notifications.upload.error.noAccessMessage`
      })
    };
  }
  switch (errorType) {
    case FileUploadErrorType.VIRUS_FOUND:
      return {
        'data-error-type': FileUploadErrorType.VIRUS_FOUND,
        titleText,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `FileManagerCore.notifications.upload.error.virusFoundMessage`,
          options: {
            fileName
          }
        })
      };
    case FileUploadErrorType.DISALLOWED_EXTENSION:
      return {
        'data-error-type': FileUploadErrorType.DISALLOWED_EXTENSION,
        titleText,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "FileManagerCore.notifications.upload.error.disallowedExtension",
          options: {
            extension
          }
        })
      };
    case FileUploadErrorType.CMS_FREE_MAX_UPLOAD_SIZE_EXCEEDED:
      return {
        'data-error-type': FileUploadErrorType.CMS_FREE_MAX_UPLOAD_SIZE_EXCEEDED,
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "FileManagerCore.notifications.upload.error.fileTooLarge.title"
        }),
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "FileManagerCore.notifications.upload.error.fileTooLarge.cmsFreeMessage_jsx",
          elements: {
            FileSize: FormattedSize
          },
          options: {
            size: maxSize
          }
        })
      };
    case FileUploadErrorType.MAX_UPLOAD_SIZE_EXCEEDED:
      return {
        'data-error-type': FileUploadErrorType.MAX_UPLOAD_SIZE_EXCEEDED,
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "FileManagerCore.notifications.upload.error.fileTooLarge.title"
        }),
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "FileManagerCore.notifications.upload.error.fileTooLarge.message_jsx",
          elements: {
            FileSize: FormattedSize
          },
          options: {
            size: maxSize
          }
        })
      };
    default:
      if (fileSize === 0) {
        return {
          titleText,
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "FileManagerCore.notifications.upload.error.emptyFileErrorMessage",
            options: {
              fileName
            }
          })
        };
      }
      return {
        titleText,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "FileManagerCore.notifications.upload.error.unknownErrorMessage",
          options: {
            fileName
          }
        })
      };
  }
};
export const getFileTooLargeNotification = file => {
  return {
    'data-error-type': FileUploadErrorType.PRE_UPLOAD_MAX_SIZE_EXCEEDED,
    type: 'danger',
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "FileManagerCore.notifications.upload.error.fileTooLarge.titleWithName",
      options: {
        fileName: file.name
      }
    }),
    message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "FileManagerCore.notifications.upload.error.fileTooLarge.message_jsx",
      elements: {
        FileSize: FormattedSize
      },
      options: {
        size: MAX_UPLOAD_SIZE_BYTES
      }
    })
  };
};
export const getRenameFileFailedNotification = (file, err) => {
  const errorType = getErrorType(err);
  const titleText = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "FileManagerCore.notifications.renameFile.error.title"
  });
  let message = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "FileManagerCore.notifications.renameFile.error.message",
    options: {
      filename: file.get('name')
    }
  });
  if (errorType === ApiErrorTypes.FILE_MOVE_CONFLICT) {
    message = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "FileManagerCore.notifications.renameFile.error.FILE_MOVE_CONFLICT"
    });
  } else if (err.responseJSON && typeof err.responseJSON === 'object' && 'message' in err.responseJSON && typeof err.responseJSON.message === 'string' && err.responseJSON.message.includes("Name can't be more than 255 characters in length")) {
    message = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "FileManagerCore.notifications.renameFile.error.FILE_NAME_LENGTH"
    });
  }
  return {
    type: 'danger',
    titleText,
    message
  };
};
export const getUploadBatchOverLimitNotification = total => ({
  'data-error-type': FileUploadErrorType.UPLOAD_BATCH_LIMIT,
  type: 'danger',
  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "FileManagerCore.tooManyFiles.message"
  }),
  message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: "FileManagerCore.tooManyFiles.description",
    options: {
      total,
      limit: MAX_FILE_UPLOAD_TOTAL
    }
  })
});
export const getImageGenerationFailedMessage = errorMessage => {
  if (errorMessage.includes('mlnlp:image:user:daily_rolling')) {
    return 'imageGenerationDailyRateLimit';
  } else if (errorMessage.includes('mlnlp:image:user:minutely_rolling')) {
    return 'imageGenerationMinuteRateLimit';
  } else if (errorMessage.includes('Your request was rejected as a result of our safety system')) {
    // todo handle internal moderation error when implemented
    return 'imageGenerationPromptModeration';
  }
  return 'imageGeneration';
};
export const getImageGenerationFailedNotification = err => {
  if (isJsonErrorResponse(err) && typeof err.responseJSON.message === 'string') {
    return getImageGenerationFailedMessage(err.responseJSON.message);
  }
  return getFailureNotification('imageGeneration');
};